import React, { useEffect, useRef, useState } from 'react';
import chatService, { TaskMessage } from '../../services/chatService';
import { getMyUserId } from '../../utils/permissions';
import '../../assets/styles/Workflow.css';

interface MentionableUser {
  id: number;
  name: string;
}

interface TaskChatProps {
  projectId: number;
  /**
   * 'inline' (default) - the original collapsible "Chat" toggle used inside
   * a task card. Unchanged from prior behavior.
   * 'panel' - an always-open, full-height messaging-style panel used in the
   * project detail overlay's right column. No collapse toggle.
   */
  variant?: 'inline' | 'panel';
  /** Who can be @mentioned - only wired up for variant='panel'. */
  mentionableUsers?: MentionableUser[];
}

const POLL_INTERVAL_MS = 20000;
// Consecutive messages from the same sender within this window are
// visually grouped (no repeated avatar/name row).
const GROUP_WINDOW_MS = 5 * 60 * 1000;

// A mention is stored in the message text as `@[Full Name]` - the bracket
// delimiter is what makes this actually parseable for multi-word names
// ("PC Manager"), unlike the prototype's plain "@Name" placeholder text,
// which was never backed by any real parsing to begin with.
const MENTION_PATTERN = /@\[([^\]]+)\]/g;

const renderMessageWithMentions = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(MENTION_PATTERN);
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<span key={`m${key++}`} className="wf-mention">@{match[1]}</span>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

// Attachments are auth-gated, so an <img> can't just point at the download
// URL - lazily fetch the blob once per message and cache the object URL.
const AttachmentPreview: React.FC<{ message: TaskMessage }> = ({ message }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const isImage = (message.attachment_mime_type || '').startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let active = true;
    chatService.getAttachmentBlobUrl(message.id).then((url) => { if (active) setBlobUrl(url); }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id]);

  const download = async () => {
    const url = await chatService.getAttachmentBlobUrl(message.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = message.attachment_filename || 'attachment';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (isImage) {
    return blobUrl ? (
      <img
        src={blobUrl}
        alt={message.attachment_filename || 'attachment'}
        className="wf-chat-attachment-img"
        onClick={download}
      />
    ) : (
      <div className="wf-chat-attachment-loading">Loading image…</div>
    );
  }

  return (
    <button type="button" className="wf-btn" style={{ marginTop: 6 }} onClick={download}>
      📎 {message.attachment_filename || 'Download attachment'}
    </button>
  );
};

const COMMON_EMOJIS = [
  '😀', '😂', '😊', '😍', '🤔', '😅', '😉', '😎',
  '🙁', '😢', '😡', '😱', '🙌', '👏', '🙏', '👍',
  '👎', '👋', '🤝', '💪', '✅', '❌', '⚠️', '🔥',
  '🎉', '💯', '❤️', '👀', '🚀', '⏰', '📌', '💡',
];

// A short one-line preview of a message, used both in the "replying to..."
// composer bar and inside a reply's own quoted snippet.
const previewText = (m: TaskMessage): string => {
  if (m.message && m.message.trim()) {
    const plain = m.message.replace(MENTION_PATTERN, '@$1');
    return plain.length > 80 ? `${plain.slice(0, 80)}…` : plain;
  }
  if (m.attachment_filename) return `📎 ${m.attachment_filename}`;
  return '';
};

const TaskChat: React.FC<TaskChatProps> = ({ projectId, variant = 'inline', mentionableUsers = [] }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<TaskMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState<TaskMessage | null>(null);
  const myId = getMyUserId();
  const intervalRef = useRef<any>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isPanel = variant === 'panel';
  const active = isPanel || open;

  const messageById = new Map(messages.map((m) => [m.id, m]));

  const mentionMatches = mentionQuery === null
    ? []
    : mentionableUsers.filter((u) => u.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 10);

  // Growable composer - height tracks content up to the CSS max-height, past
  // which it scrolls internally instead of pushing the rest of the panel around.
  const autoGrow = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => { autoGrow(); }, [draft]);

  // Look backward from the cursor for an in-progress "@query" token (an "@"
  // not preceded by another non-space char, with no whitespace after it up
  // to the cursor) - that's what the autocomplete dropdown searches on.
  const handleDraftChange = (value: string, cursorPos: number) => {
    setDraft(value);
    const upToCursor = value.slice(0, cursorPos);
    const at = upToCursor.lastIndexOf('@');
    if (at === -1 || /\s/.test(upToCursor.slice(at + 1))) {
      setMentionQuery(null);
      setMentionStart(null);
      return;
    }
    setMentionHighlight(0);
    setMentionQuery(upToCursor.slice(at + 1));
    setMentionStart(at);
  };

  const insertMention = (user: MentionableUser) => {
    if (mentionStart === null) return;
    const cursor = inputRef.current?.selectionStart ?? draft.length;
    const before = draft.slice(0, mentionStart);
    const after = draft.slice(cursor);
    const next = `${before}@[${user.name}] ${after}`;
    setDraft(next);
    setMentionQuery(null);
    setMentionStart(null);
    requestAnimationFrame(() => {
      const pos = before.length + user.name.length + 4;
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const insertEmoji = (emoji: string) => {
    const cursor = inputRef.current?.selectionStart ?? draft.length;
    const next = `${draft.slice(0, cursor)}${emoji}${draft.slice(cursor)}`;
    setDraft(next);
    setEmojiPickerOpen(false);
    requestAnimationFrame(() => {
      const pos = cursor + emoji.length;
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const load = () => {
    chatService.listMessages(projectId)
      .then((msgs) => { setMessages(msgs); setError(null); })
      .catch((e) => setError((e?.response?.data?.message || e?.response?.message) || 'Failed to load chat'));
  };

  useEffect(() => {
    if (!active) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, projectId]);

  const send = async () => {
    if (!draft.trim()) return;
    const text = draft;
    const replyId = replyingTo?.id ?? null;
    setDraft('');
    setReplyingTo(null);
    try {
      await chatService.postMessage(projectId, text, replyId);
      load();
    } catch (e: any) {
      setDraft(text);
      setReplyingTo(replyId ? messageById.get(replyId) || null : null);
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to send message');
    }
  };

  if (isPanel) {
    return (
      <div className="wf-chat-full">
        <div className="wf-chatlog">
          {messages.length === 0 ? (
            <div className="wf-chat-empty">
              <div className="wf-chat-empty-icon">💬</div>
              <div className="wf-chat-empty-title">No messages yet</div>
              <div className="wf-chat-empty-sub">Start the conversation — anyone on this project can see it here.</div>
            </div>
          ) : (
            messages.map((m, i) => {
              const isOwn = m.user_id === myId;
              const prev = messages[i - 1];
              const grouped = !!prev
                && prev.user_id === m.user_id
                && (new Date(m.created_at).getTime() - new Date(prev.created_at).getTime()) < GROUP_WINDOW_MS;
              const name = m.user_name || `User #${m.user_id}`;
              const initial = name ? name.charAt(0).toUpperCase() : '?';
              const avatarColorClass = `wf-chat-avatar-c${Math.abs(m.user_id) % 6}`;
              const time = new Date(m.created_at).toLocaleString();
              const rowClass = `wf-chat-msg-row${isOwn ? ' wf-chat-msg-row-own' : ''}${grouped ? ' wf-chat-msg-row-grouped' : ''}`;

              const repliedTo = m.reply_to_message_id != null ? messageById.get(m.reply_to_message_id) : undefined;

              return (
                <div key={m.id} className={rowClass}>
                  {!isOwn && (
                    grouped
                      ? <span className="wf-chat-avatar-spacer" />
                      : <span className={`wf-chat-avatar ${avatarColorClass}`}>{initial}</span>
                  )}
                  <div className="wf-chat-msg-col">
                    {isOwn && <div className="wf-chat-time-own">{time}</div>}
                    {!isOwn && !grouped && (
                      <div className="wf-chat-name2">
                        {name}
                        <span className="wf-chat-time2">{time}</span>
                      </div>
                    )}
                    <div className={`wf-chat-bubble2${isOwn ? ' wf-chat-bubble2-own' : ''}`}>
                      {repliedTo && (
                        <div className="wf-chat-reply-quote">
                          <div className="wf-chat-reply-quote-name">{repliedTo.user_id === myId ? 'You' : (repliedTo.user_name || `User #${repliedTo.user_id}`)}</div>
                          <div className="wf-chat-reply-quote-text">{previewText(repliedTo)}</div>
                        </div>
                      )}
                      {renderMessageWithMentions(m.message)}
                      {m.attachment_filename && <AttachmentPreview message={m} />}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="wf-chat-reply-btn"
                    title="Reply"
                    onClick={() => { setReplyingTo(m); inputRef.current?.focus(); }}
                  >
                    ↩
                  </button>
                </div>
              );
            })
          )}
        </div>
        {error && <div className="wf-chat-error">{error}</div>}
        {replyingTo && (
          <div className="wf-chat-reply-bar">
            <div className="wf-chat-reply-bar-body">
              <div className="wf-chat-reply-bar-name">
                Replying to {replyingTo.user_id === myId ? 'yourself' : (replyingTo.user_name || `User #${replyingTo.user_id}`)}
              </div>
              <span className="wf-chat-reply-bar-text">{previewText(replyingTo)}</span>
            </div>
            <button type="button" className="wf-chat-reply-bar-close" onClick={() => setReplyingTo(null)} title="Cancel reply">✕</button>
          </div>
        )}
        <div className="wf-chat-input-row2">
          {mentionMatches.length > 0 && (
            <div className="wf-mention-dropdown">
              {mentionMatches.map((u, idx) => (
                <div
                  key={u.id}
                  className={`wf-mention-option${idx === mentionHighlight ? ' active' : ''}`}
                  onMouseEnter={() => setMentionHighlight(idx)}
                  onClick={() => insertMention(u)}
                >
                  {u.name}
                </div>
              ))}
            </div>
          )}
          {emojiPickerOpen && (
            <div className="wf-emoji-picker">
              {COMMON_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="wf-emoji-option"
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="wf-btn wf-emoji-btn"
            onClick={() => setEmojiPickerOpen((o) => !o)}
            title="Insert emoji"
          >
            😊
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            placeholder="Message... use @Name to mention"
            value={draft}
            onChange={(e) => handleDraftChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onFocus={() => setEmojiPickerOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setMentionQuery(null); setMentionStart(null); setEmojiPickerOpen(false); return; }
              if (mentionMatches.length > 0) {
                if (e.key === 'ArrowDown') { e.preventDefault(); setMentionHighlight((i) => (i + 1) % mentionMatches.length); return; }
                if (e.key === 'ArrowUp') { e.preventDefault(); setMentionHighlight((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); return; }
                if (e.key === 'Enter') { e.preventDefault(); insertMention(mentionMatches[mentionHighlight]); return; }
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <button type="button" className="wf-btn wf-btn-primary" onClick={send}>Send</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="wf-chat-toggle" onClick={() => setOpen(!open)}>
        {open ? 'Hide chat' : `Chat${messages.length ? ` (${messages.length})` : ''}`}
      </span>
      {open && (
        <div className="wf-chat-panel">
          <div className="wf-chat-log">
            {messages.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af' }}>No messages yet.</div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="wf-chat-msg">
                  {m.user_id === myId ? 'You' : `User #${m.user_id}`}: {m.message}
                  <span className="wf-chat-meta">{new Date(m.created_at).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
          <div className="wf-chat-input-row">
            <input
              type="text"
              placeholder="Message..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            />
            <button type="button" className="wf-btn wf-btn-primary" onClick={send}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskChat;
